import { NavLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import Logo from "@/mockups/Logo";
import {
  LayoutDashboard, Building2, Contact, FolderKanban,
  Clock, ShieldCheck, IdCard, Wallet,
  UsersRound, CalendarClock, CalendarDays, CalendarRange, Plug, Timer,
  PanelLeftClose, PanelLeftOpen, Coins, Receipt, Banknote, BarChart3, Target, ArrowLeftRight, BookOpen,
  UtensilsCrossed, Store, CalendarCheck, ClipboardList, Upload, Layers, History, Send, DatabaseBackup,
} from "lucide-react";
import { cn } from "@/lib/utils";

const CASHFLOW_ROLES = ["Administrador", "Administración"];
const COBROS_ROLES = ["Administrador", "Directora", "Administración"];
const BUDGET_ROLES = ["Administrador", "Directora"];
const RH_ADMIN_ROLES = ["Administrador", "Administración"];

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, module: "dashboard" },
  { section: "Operaciones" },
  { to: "/proyectos", label: "Proyectos", icon: FolderKanban, module: "projects" },
  { to: "/presupuestos", label: "Presupuestos", icon: Receipt, module: "projects" },
  { to: "/informes/cierres", label: "Cierre de proyectos", icon: Target, roles: BUDGET_ROLES },
  { to: "/horas", label: "Horas", icon: Clock, module: "timesheets" },
  { to: "/pagos-externos", label: "Pagos externos", icon: ArrowLeftRight, module: "external_payments" },
  { section: "CRM" },
  { to: "/crm/empresas", label: "Empresas", icon: Building2, module: "crm" },
  { to: "/crm/contactos", label: "Contactos", icon: Contact, module: "crm" },
  { section: "Dining" },
  { to: "/restauracion/dashboard", label: "Resumen", icon: LayoutDashboard, module: "dining" },
  { to: "/restauracion/restaurantes", label: "Restaurantes", icon: Store, module: "dining" },
  { to: "/restauracion/eventos", label: "Eventos", icon: CalendarCheck, module: "dining" },
  { to: "/restauracion/asistencia", label: "Asistencia", icon: ClipboardList, module: "dining" },
  { to: "/restauracion/facturas", label: "Facturas", icon: Receipt, module: "dining" },
  { to: "/restauracion/cashout", label: "Cashout", icon: Wallet, module: "dining" },
  { to: "/restauracion/presupuesto", label: "Presupuesto", icon: Coins, module: "dining" },
  { to: "/restauracion/empleados", label: "Empleados", icon: UsersRound, module: "dining" },
  { to: "/restauracion/waves", label: "Waves", icon: Layers, module: "dining" },
  { to: "/restauracion/enviar", label: "Enviar a Proyectos", icon: Send, module: "dining" },
  { to: "/restauracion/importar", label: "Importar Excel", icon: Upload, module: "dining" },
  // --- Sección "Tesorería" OCULTA (Julio 2026, división de arquitectura) ---
  // Cashflow y Gastos Fijos se gestionan ahora en la app externa CashFlow_Grabarte,
  // que consume los datos de esta app vía la API de integración (/api/integration/*).
  // Ocultado del menú (reversible): rutas, endpoints y datos siguen intactos.
  // { section: "Tesorería" },
  // { external: true, label: "Cashflow", icon: Coins, href: "/api/tesoreria-ui", roles: CASHFLOW_ROLES },
  // { to: "/tesoreria/cobros-pagos", label: "Cobros y pagos", icon: ArrowLeftRight, roles: COBROS_ROLES },
  // { to: "/tesoreria/gastos-fijos", label: "Gastos Fijos", icon: Receipt, roles: CASHFLOW_ROLES },
  { section: "Portal del empleado" },
  { to: "/portal/mi-ficha", label: "Mi ficha", icon: IdCard, always: true },
  { to: "/portal/agenda", label: "Mi agenda", icon: CalendarRange, always: true },
  { to: "/portal/nominas", label: "Nómina", icon: Wallet, always: true },
  { to: "/portal/festivos", label: "Festivos", icon: CalendarDays, always: true },
  { to: "/rrhh/registro-horario", label: "Registro horario", icon: Timer, rhGated: true },
  { section: "RRHH" },
  { to: "/rrhh/equipo", label: "Equipo", icon: UsersRound, module: "hr" },
  // --- Nóminas + Resumen salarios OCULTOS (Julio 2026): la gestión de nóminas y el
  // informe de salarios se hacen ahora en la app externa Cashflow (SSoT). El Portal
  // del empleado ("Nómina") sigue aquí pero lee las nóminas desde la API de Cashflow.
  // { to: "/rrhh/nominas", label: "Nóminas", icon: Banknote, module: "hr" },
  // { to: "/rrhh/resumen-salarios", label: "Resumen salarios", icon: BarChart3, module: "hr" },
  { to: "/rrhh/vacaciones", label: "Solicitudes", icon: CalendarClock, module: "hr" },
  { section: "Administración" },
  { to: "/permisos", label: "Permisos", icon: ShieldCheck, module: "settings" },
  { to: "/ajustes", label: "Integraciones", icon: Plug, module: "settings" },
  { to: "/copias-seguridad", label: "Copias de seguridad", icon: DatabaseBackup, roles: ["Administrador"] },
  { section: "Ayuda" },
  { to: "/manual", label: "Manual de uso", icon: BookOpen, always: true },
];

export default function Sidebar({ onNavigate, collapsed = false, onToggleCollapse }) {
  const { can, user } = useAuth();
  const { data: rhCfg } = useQuery({
    queryKey: ["rh-config-nav"],
    queryFn: async () => (await api.get("/rh/config")).data,
    retry: false, staleTime: 5 * 60 * 1000,
  });
  const rhEnabled = !!rhCfg?.enabled;
  const isRhAdmin = RH_ADMIN_ROLES.includes(user?.role);

  // Build visible groups (hide empty sections)
  const groups = [];
  let current = { section: null, items: [] };
  NAV.forEach((it) => {
    if (it.section) {
      if (current.items.length) groups.push(current);
      current = { section: it.section, items: [] };
    } else if (it.external) {
      if (it.roles.includes(user?.role)) current.items.push(it);
    } else if (it.rhGated) {
      if (rhEnabled || isRhAdmin) current.items.push(it);
    } else if (it.roles) {
      if (it.roles.includes(user?.role)) current.items.push(it);
    } else if (it.always || can(it.module, "view")) {
      current.items.push(it);
    }
  });
  if (current.items.length) groups.push(current);

  const itemClasses = (isActive) =>
    cn(
      "flex items-center rounded-md text-sm font-medium transition-colors duration-200 relative",
      collapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2",
      isActive ? "bg-white/[0.07] text-white" : "text-white/60 hover:bg-white/[0.04] hover:text-white"
    );

  return (
    <aside className={cn("shrink-0 h-full bg-navy flex flex-col transition-[width] duration-200", collapsed ? "w-16" : "w-60")}>
      <div className={cn("h-16 flex items-center border-b border-white/10", collapsed ? "justify-center px-0" : "justify-between px-5")}>
        {!collapsed && <Logo variant="light" />}
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            data-testid="sidebar-toggle"
            title={collapsed ? "Expandir menú" : "Colapsar menú"}
            className="text-white/50 hover:text-white transition-colors duration-200 p-1.5 rounded-md hover:bg-white/[0.06]"
          >
            {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </button>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
        {groups.map((g, gi) => (
          <div key={gi}>
            {g.section && (
              collapsed
                ? gi > 0 && <div className="mx-2 my-2 border-t border-white/10" />
                : <div className="px-3 pt-5 pb-1.5 text-[10px] font-semibold tracking-[0.18em] uppercase text-white/35">
                    {g.section}
                  </div>
            )}
            {g.items.map((item) =>
              item.external ? (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={onNavigate}
                  title={item.label}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={itemClasses(false)}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  {!collapsed && item.label}
                </a>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.exact}
                  onClick={onNavigate}
                  title={collapsed ? item.label : undefined}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) => itemClasses(isActive)}
                >
                  {({ isActive }) => (
                    <>
                      {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-gold" />}
                      <item.icon className={cn("h-4 w-4 shrink-0", isActive && "text-gold")} />
                      {!collapsed && item.label}
                    </>
                  )}
                </NavLink>
              )
            )}
          </div>
        ))}
      </nav>
    </aside>
  );
}
